import dayjs from "dayjs";

type SummableKey = Exclude<keyof IGetEmailDashboardRawData, "month">;

function sumColumns<K extends SummableKey>(
  keys: K[],
  data: IGetEmailDashboardRawData[],
): Record<K, number> {
  // Initialise all requested keys with 0
  const initial = keys.reduce(
    (accumulator, key) => {
      accumulator[key] = 0;
      return accumulator;
    },
    {} as Record<K, number>,
  );

  return data.reduce((accumulator, row) => {
    for (const key of keys) {
      accumulator[key] += row[key] ?? 0;
    }
    return accumulator;
  }, initial);
}

function cumulativeSumColumns<K extends SummableKey>(
  keys: K[],
  data: IGetEmailDashboardRawData[],
): Array<Record<K, number> & { month: string }> {
  // Running total of all keys
  const runningTotals = keys.reduce(
    (accumulator, key) => {
      accumulator[key] = 0;
      return accumulator;
    },
    {} as Record<K, number>,
  );

  // Build cumulative array
  return data.map(row => {
    // Friendly month
    const friendlyMonth = dayjs(row.month).format("MMM YY");

    // Update running totals
    for (const key of keys) {
      runningTotals[key] += row[key] ?? 0;
    }

    // Return a snapshot of totals for this month
    return {
      month: friendlyMonth,
      ...runningTotals,
    };
  });
}

const formatNumber = (number: number): string => {
  return new Intl.NumberFormat("en-US", {
    notation: "standard",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number);
};

export const getTopLevelCardsStats = (
  data: IGetEmailDashboardRS["rawData"],
) => {
  const {
    verifiedContacts,
    marketableContacts,
    netNewMarketableContacts,
    netNewRepeatPurchases,
    netNewRepeatPurchaseRevenue,
  } = sumColumns(
    [
      "verifiedContacts",
      "marketableContacts",
      "netNewMarketableContacts",
      "netNewRepeatPurchases",
      "netNewRepeatPurchaseRevenue",
    ],
    data,
  );

  /**
   * Get the Marketing Consent Rate
   */
  const marketingConsentRate =
    verifiedContacts > 0
      ? Math.round((marketableContacts / verifiedContacts) * 100)
      : 0;

  /**
   * Format the repeat puchase revenue to the .00 place.
   */
  const repeatPurchaseRevenue = parseFloat(
    netNewRepeatPurchaseRevenue.toFixed(2),
  );

  return {
    marketingConsentRate: formatNumber(marketingConsentRate),
    verifiedContacts: formatNumber(verifiedContacts),
    subscribedContacts: formatNumber(marketableContacts),
    incrementalSubscribers: formatNumber(netNewMarketableContacts),
    repeatPurchases: formatNumber(netNewRepeatPurchases),
    repeatPurchaseRevenue: formatNumber(repeatPurchaseRevenue),
  };
};

export const getMcrSummaryTableStats = (
  data: IGetEmailDashboardRS["rawData"],
  preDatashipsMarketableRate: number,
) => {
  const { verifiedContacts, marketableContacts } = sumColumns(
    ["verifiedContacts", "marketableContacts"],
    data,
  );

  /**
   * Get the Marketing Consent Rate
   */
  const marketingConsentRate =
    verifiedContacts > 0
      ? Math.round((marketableContacts / verifiedContacts) * 100)
      : 0;

  /**
   * Work out their subscribed contacts count if they had their pre
   * Dataships MCR in place.
   */
  const preDatashipsSubscribedContacts = Math.round(
    verifiedContacts * (preDatashipsMarketableRate / 100),
  );

  /**
   * Work out the pre and post non-subscribed contacts count
   */
  const nonSubscribedContacts = verifiedContacts - marketableContacts;
  const preDatashipsNonSubscribedContacts =
    verifiedContacts - preDatashipsSubscribedContacts;

  return {
    marketingConsentRate: formatNumber(marketingConsentRate),
    preDatashipsMarketingConsentRate: formatNumber(preDatashipsMarketableRate),
    verifiedContacts: formatNumber(verifiedContacts),
    subscribedContacts: formatNumber(marketableContacts),
    preDatashipsSubscribedContacts: formatNumber(
      preDatashipsSubscribedContacts,
    ),
    nonSubscribedContacts: formatNumber(nonSubscribedContacts),
    preDatashipsNonSubscribedContacts: formatNumber(
      preDatashipsNonSubscribedContacts,
    ),
  };
};

export const getMonthlyBreakdownTableRowData = (
  data: IGetEmailDashboardRawData,
) => {
  const {
    month,
    verifiedContacts,
    marketableContacts,
    netNewMarketableContacts,
    netNewRepeatPurchases,
    netNewRepeatPurchaseRevenue,
  } = data;

  const friendlyMonth = dayjs(month).format("MMM YY");

  /**
   * Get the Marketing Consent Rate
   */
  const marketingConsentRate =
    verifiedContacts > 0
      ? Math.round((marketableContacts / verifiedContacts) * 100)
      : 0;

  return {
    month: friendlyMonth,
    verifiedContacts: formatNumber(verifiedContacts),
    marketingConsentRate: formatNumber(marketingConsentRate),
    subscribedContacts: formatNumber(marketableContacts),
    incrementalSubscribers: formatNumber(netNewMarketableContacts),
    repeatPurchases: formatNumber(netNewRepeatPurchases),
    repeatPurchaseRevenue: formatNumber(netNewRepeatPurchaseRevenue),
  };
};

export const getMonthlyMcrMovements = (
  data: IGetEmailDashboardRS["rawData"],
) => {
  const cumulativeData = cumulativeSumColumns(
    ["verifiedContacts", "marketableContacts"],
    data,
  );

  return cumulativeData?.map(row => {
    const { month, verifiedContacts, marketableContacts } = row;

    /**
     * Get the Marketing Consent Rate
     */
    const marketingConsentRate =
      verifiedContacts > 0
        ? Math.round((marketableContacts / verifiedContacts) * 100)
        : 0;

    return {
      name: month,
      value: marketingConsentRate,
    };
  });
};

export const getContactSummary = (data: IGetEmailDashboardRS["rawData"]) =>
  cumulativeSumColumns(["verifiedContacts", "marketableContacts"], data);

export const getIncrementalSubscribers = (
  data: IGetEmailDashboardRS["rawData"],
) => cumulativeSumColumns(["netNewMarketableContacts"], data);

export const getRepeatPurchaseRevenue = (
  data: IGetEmailDashboardRS["rawData"],
) => cumulativeSumColumns(["netNewRepeatPurchaseRevenue"], data);
